import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  LockReset as LockResetIcon,
  ForwardToInbox as ForwardToInboxIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import MainLayout from '../components/MainLayout';
import { PageHeader } from '../components/ui/PageHeader';
import { Dropdown } from '../components/ui/Dropdown';
import { apiService } from '../services/apiService';
import { uiConfig } from '../config';
import { getSubscription, incrementUsedSeats, decrementUsedSeats, getPendingChanges } from '../services/billingService';
import { SubscriptionData } from '../types/subscription';
import moment from 'moment';
import { DATE_FORMAT } from '../constants/dateFormats';
import { FILTER_SEARCH_WIDTH_PX, FILTER_TEXT_SX } from '../constants/formLayout';

interface User {
  id: string;
  _id: string;
  firebaseUid: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  isActive: boolean;
  default?: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

interface CreateUserForm {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  role: 'admin' | 'user';
  sendWelcomeEmail: boolean;
}

interface EditUserForm {
  name: string;
  role: 'admin' | 'user';
  isActive: boolean;
}

/** A filter-row search: fixed width and 14px text, like the dropdowns beside it. */
const USER_SEARCH_SX = { width: FILTER_SEARCH_WIDTH_PX, ...FILTER_TEXT_SX } as const;

const ROLE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const ROLE_OPTIONS = [
  { value: 'user', label: 'User' },
  { value: 'admin', label: 'Admin' },
];

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [pendingChanges, setPendingChanges] = useState<any>(null);
  const [seatLimitDialogOpen, setSeatLimitDialogOpen] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(uiConfig.defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resendWelcomeDialogOpen, setResendWelcomeDialogOpen] = useState(false);
  const [resendingWelcomeEmail, setResendingWelcomeEmail] = useState(false);

  // Selected user
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Track which user is being toggled
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);

  // Loading state for creating user
  const [creatingUser, setCreatingUser] = useState(false);

  // Forms
  const [createForm, setCreateForm] = useState<CreateUserForm>({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'user',
    sendWelcomeEmail: true
  });
  
  const [editForm, setEditForm] = useState<EditUserForm>({
    name: '',
    role: 'user',
    isActive: true
  });

  const [newPassword, setNewPassword] = useState('');

  // Upgrade dialog state
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  // Fetch subscription
  const fetchSubscription = async () => {
    try {
      const data = await getSubscription();
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  // Note: handleAddUserClick is defined later in the component to handle seat availability checks

  useEffect(() => {
    fetchSubscription();
  }, []);

  // Fetch users
  const fetchUsers = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Also refresh subscription data to get latest seat counts
      fetchSubscription();

      // Fetch pending changes to show pending deactivation badges
      try {
        const pending = await getPendingChanges();
        setPendingChanges(pending);
      } catch (pendingError) {
        console.error('Error fetching pending changes:', pendingError);
        // Don't fail the whole fetch if pending changes fail
      }

      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('limit', String(rowsPerPage));
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      if (roleFilter !== 'all') {
        params.append('role', roleFilter);
      }
      
      if (statusFilter !== 'all') {
        params.append('isActive', statusFilter === 'active' ? 'true' : 'false');
      }
      
      const response = await apiService.get(`/admin/users?${params.toString()}`);
      
      if (response.success) {
        setUsers(response.data.users);
        setTotalCount(response.data.pagination.total);
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setError(error.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, roleFilter, statusFilter]);
  
  useEffect(() => {
    let isSubscribed = true;
    
    const loadUsers = async () => {
      if (isSubscribed) {
        await fetchUsers();
      }
    };
    
    loadUsers();
    
    // Cleanup function to prevent state updates if component unmounts or effect re-runs
    return () => {
      isSubscribed = false;
    };
  }, [fetchUsers]);
  
  // Handle create user
  const handleCreateUser = async () => {
    try {
      setCreatingUser(true);
      setError(null);

      const wasWelcomeEmailRequested = createForm.sendWelcomeEmail;
      const response = await apiService.post('/admin/users', createForm);

      if (response.success) {
        if (wasWelcomeEmailRequested && response.data?.welcomeEmailSent === false) {
          setWarning('User created successfully, but the welcome email could not be sent. Please share the access details with the user manually.');
        } else {
          setSuccess('User created successfully. Access has been granted to this user.');
        }
        setCreateDialogOpen(false);
        setCreateForm({ email: '', password: '', confirmPassword: '', name: '', role: 'user', sendWelcomeEmail: true });

        // Increment used seats
        try {
          await incrementUsedSeats();
          await fetchSubscription(); // Refresh subscription data
        } catch (seatError) {
          console.error('Error updating seat count:', seatError);
        }

        fetchUsers();
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      setError(error.message || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };
  
  // Handle edit user
  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    try {
      setError(null);
      
      const response = await apiService.put(`/admin/users/${selectedUser._id || selectedUser.id}`, editForm);
      
      if (response.success) {
        setSuccess('User updated successfully');
        setEditDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      setError(error.message || 'Failed to update user');
    }
  };
  
  // Handle delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setError(null);

      const response = await apiService.delete(`/admin/users/${selectedUser._id || selectedUser.id}`);

      if (response.success) {
        setSuccess('User deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedUser(null);

        // Decrement used seats
        try {
          await decrementUsedSeats();
          await fetchSubscription(); // Refresh subscription data
        } catch (seatError) {
          console.error('Error updating seat count:', seatError);
        }

        fetchUsers();
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setError(error.message || 'Failed to delete user');
    }
  };
  
  // Handle toggle user status
  const handleToggleUserStatus = async (user: User) => {
    const userId = user._id || user.id;

    // If reactivating user, check seat availability first (skipped when trial is active)
    if (!user.isActive && subscription && !subscription?.trialAccess?.enabled) {
      const totalSeats = subscription.subscription.totalUserSeats;
      const usedSeats = subscription.subscription.usedUserSeats || 0;

      if (usedSeats >= totalSeats) {
        setError(`Cannot reactivate user. All ${totalSeats} seats are in use. Please purchase additional seats first.`);
        setSeatLimitDialogOpen(true);
        return;
      }
    }

    try {
      setError(null);
      setTogglingUserId(userId);

      const response = await apiService.post(`/admin/users/${userId}/toggle`);

      if (response.success) {
        setSuccess(`User ${user.isActive ? 'disabled' : 'enabled'} successfully`);

        // Refresh subscription data to get updated seat counts from backend
        await fetchSubscription();

        fetchUsers();
      }
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      setError(error.message || 'Failed to toggle user status');
    } finally {
      setTogglingUserId(null);
    }
  };
  
  // Handle reset password
  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    try {
      setError(null);
      
      const response = await apiService.post(`/admin/users/${selectedUser._id || selectedUser.id}/reset-password`, {
        newPassword
      });
      
      if (response.success) {
        setSuccess('Password reset successfully');
        setResetPasswordDialogOpen(false);
        setSelectedUser(null);
        setNewPassword('');
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);
      setError(error.message || 'Failed to reset password');
    }
  };
  
  // Handle resend welcome email (never-logged-in users only)
  const handleResendWelcomeEmail = async () => {
    if (!selectedUser) return;

    try {
      setResendingWelcomeEmail(true);
      setError(null);

      const response = await apiService.post(
        `/admin/users/${selectedUser._id || selectedUser.id}/resend-welcome`
      );

      if (response?.success) {
        setSuccess(
          `Welcome email sent to ${response.data?.sentTo || selectedUser.email} with a new temporary password.`
        );
        setResendWelcomeDialogOpen(false);
        setSelectedUser(null);
      }
    } catch (error: any) {
      console.error('Error resending welcome email:', error);
      setError(error?.message || 'Failed to resend the welcome email');
    } finally {
      setResendingWelcomeEmail(false);
    }
  };

  // Open resend welcome email dialog
  const openResendWelcomeDialog = (user: User) => {
    setSelectedUser(user);
    setResendWelcomeDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      role: user.role,
      isActive: user.isActive
    });
    setEditDialogOpen(true);
  };
  
  // Open delete dialog
  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };
  
  // Open reset password dialog
  const openResetPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setResetPasswordDialogOpen(true);
  };

  // Check if user can be added
  const isTrialActive = subscription?.trialAccess?.enabled === true;
  const canAddUser = isTrialActive
    ? true
    : subscription?.subscription
    ? (subscription.subscription.usedUserSeats || 0) < (subscription.subscription.totalUserSeats || 1)
    : true; // Allow adding users if subscription data isn't loaded yet

  // Handle add user click
  const handleAddUserClick = () => {
    if (!canAddUser) {
      setSeatLimitDialogOpen(true);
    } else {
      setCreateDialogOpen(true);
    }
  };

  return (
    <MainLayout title="Users Management">
      <div className="p-2">
        <PageHeader
          title="User Management"
          description={subscription?.subscription
            ? (isTrialActive
                ? `${subscription.subscription.usedUserSeats || 0} seats used (unlimited — trial active)`
                : `${subscription.subscription.usedUserSeats || 0} / ${subscription.subscription.totalUserSeats || 1} seats used`)
            : undefined}
          actions={(
            <>
          <IconButton onClick={fetchUsers} color="primary" aria-label="Refresh users">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddUserClick}
          >
            Add User
          </Button>
            </>
          )}
        />
      
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {warning && (
        <Alert severity="warning" onClose={() => setWarning(null)} sx={{ mb: 2 }}>
          {warning}
        </Alert>
      )}
      
      {/* Filters sit on the page background, right-aligned (DESIGN_SYSTEM.md, filter rows). */}
      <div className="mb-6 flex flex-wrap items-center justify-end gap-2">
        <TextField
          size="small"
          placeholder="Search by email or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          type="search"
          // Chrome's password manager pairs the create-dialog's password
          // fields with the nearest text input and autofills the saved
          // admin email here; the name + autocomplete opt this field out.
          inputProps={{ autoComplete: 'off', name: 'user-roster-search' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={USER_SEARCH_SX}
        />

        <Dropdown
          label="Role"
          width="narrow"
          value={roleFilter}
          onChange={setRoleFilter}
          options={ROLE_FILTER_OPTIONS}
        />

        <Dropdown
          label="Status"
          width="narrow"
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_FILTER_OPTIONS}
        />
      </div>

      <Paper sx={{ p: 3 }}>
        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user._id || user.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {user.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                        {user.name}
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={user.role === 'admin' ? 'secondary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'error'}
                          size="small"
                        />
                        {pendingChanges?.hasPending &&
                         pendingChanges.seatReduction?.usersToDeactivate?.some(
                           (u: any) => u.firebaseUid === user.firebaseUid
                         ) && (
                          <Tooltip title={`Will be deactivated on ${moment(pendingChanges.seatReduction.scheduledFor * 1000).format(DATE_FORMAT)}`}>
                            <Chip
                              label="Pending Deactivation"
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {user.lastLogin
                        ? moment(user.lastLogin).format(DATE_FORMAT)
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      {moment(user.createdAt).format(DATE_FORMAT)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(user)}
                          disabled={togglingUserId !== null}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Password">
                        <IconButton
                          size="small"
                          onClick={() => openResetPasswordDialog(user)}
                          disabled={togglingUserId !== null}
                        >
                          <LockResetIcon />
                        </IconButton>
                      </Tooltip>
                      {!user?.lastLogin && (
                        <Tooltip title="Resend Welcome Email">
                          <IconButton
                            size="small"
                            aria-label="Resend welcome email"
                            onClick={() => openResendWelcomeDialog(user)}
                            disabled={togglingUserId !== null}
                          >
                            <ForwardToInboxIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!user.default && (
                        <Tooltip
                          title={
                            togglingUserId === (user._id || user.id)
                              ? 'Processing...'
                              : (!user.isActive && !isTrialActive && subscription && (subscription.subscription.usedUserSeats >= subscription.subscription.totalUserSeats))
                                ? 'No seats available - Purchase more seats to enable this user'
                                : (user.isActive ? 'Disable' : 'Enable')
                          }
                        >
                          <Box display="inline-flex" alignItems="center">
                            {togglingUserId === (user._id || user.id) ? (
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                            ) : (
                              <Switch
                                checked={user.isActive}
                                onChange={() => handleToggleUserStatus(user)}
                                size="small"
                                disabled={togglingUserId !== null}
                                color={
                                  !user.isActive && !isTrialActive && subscription &&
                                  (subscription.subscription.usedUserSeats >= subscription.subscription.totalUserSeats)
                                    ? 'error'
                                    : 'primary'
                                }
                              />
                            )}
                          </Box>
                        </Tooltip>
                      )}
                      {!user.default && user.isActive && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => openDeleteDialog(user)}
                            color="error"
                            disabled={togglingUserId !== null}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
      
      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creatingUser && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(e) =>
                setCreateForm({ ...createForm, email: e.target.value })
              }
              fullWidth
              required
              // This is someone else's address — never the admin's own login.
              inputProps={{ autoComplete: 'off', name: 'new-user-email' }}
            />
            <TextField
              label="Password"
              type="password"
              value={createForm.password}
              onChange={(e) =>
                setCreateForm({ ...createForm, password: e.target.value })
              }
              fullWidth
              required
              helperText="Minimum 6 characters"
              inputProps={{ autoComplete: 'new-password' }}
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={createForm.confirmPassword}
              onChange={(e) =>
                setCreateForm({ ...createForm, confirmPassword: e.target.value })
              }
              fullWidth
              required
              inputProps={{ autoComplete: 'new-password' }}
              error={createForm.confirmPassword.length > 0 && createForm.password !== createForm.confirmPassword}
              helperText={
                createForm.confirmPassword.length > 0 && createForm.password !== createForm.confirmPassword
                  ? "Passwords do not match"
                  : ""
              }
            />
            <TextField
              label="Name"
              value={createForm.name}
              onChange={(e) =>
                setCreateForm({ ...createForm, name: e.target.value })
              }
              fullWidth
              required
            />
            <Dropdown
              label="Role"
              size="medium"
              width="field"
              value={createForm.role}
              onChange={(role) =>
                setCreateForm({
                  ...createForm,
                  role: role as 'admin' | 'user'
                })
              }
              options={ROLE_OPTIONS}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={createForm.sendWelcomeEmail}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, sendWelcomeEmail: e.target.checked })
                  }
                />
              }
              label="Send welcome email with access details"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creatingUser}>Cancel</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={
              creatingUser ||
              !createForm.email ||
              !createForm.password ||
              !createForm.confirmPassword ||
              !createForm.name ||
              createForm.password.length < 6 ||
              createForm.password !== createForm.confirmPassword
            }
            startIcon={creatingUser ? <CircularProgress size={20} /> : null}
          >
            {creatingUser ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Name"
              value={editForm.name}
              onChange={(e) =>
                setEditForm({ ...editForm, name: e.target.value })
              }
              fullWidth
              required
            />
            <Dropdown
              label="Role"
              size="medium"
              width="field"
              value={editForm.role}
              onChange={(role) =>
                setEditForm({
                  ...editForm,
                  role: role as 'admin' | 'user'
                })
              }
              options={ROLE_OPTIONS}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isActive}
                  onChange={(e) =>
                    setEditForm({ ...editForm, isActive: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditUser}
            variant="contained"
            disabled={!editForm.name}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => setResetPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Reset password for: {selectedUser?.email}
            </Typography>
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
              helperText="Minimum 6 characters"
              inputProps={{ autoComplete: 'new-password' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="warning"
            disabled={!newPassword || newPassword.length < 6}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Resend Welcome Email Dialog */}
      <Dialog
        open={resendWelcomeDialogOpen}
        onClose={() => !resendingWelcomeEmail && setResendWelcomeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resend Welcome Email</DialogTitle>
        <DialogContent>
          <Typography>
            Resend the welcome email to "{selectedUser?.name}" ({selectedUser?.email})?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            This will generate a NEW temporary password and email it to the user
            along with their access details. Their previous temporary password will
            stop working, and they will be asked to change the password when they
            first log in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResendWelcomeDialogOpen(false)}
            disabled={resendingWelcomeEmail}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResendWelcomeEmail}
            variant="contained"
            disabled={resendingWelcomeEmail}
            startIcon={resendingWelcomeEmail ? <CircularProgress size={20} /> : null}
          >
            {resendingWelcomeEmail ? 'Sending...' : 'Resend Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.name}" ({selectedUser?.email})?
            This action will disable their account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seat Limit Dialog - Legacy */}
      <Dialog
        open={seatLimitDialogOpen}
        onClose={() => setSeatLimitDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>User Seat Limit Reached</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            You've reached your subscription's user seat limit ({subscription?.subscription.totalUserSeats || 1} seats).
          </Typography>
          <Typography variant="body2" color="textSecondary">
            To add more users, please upgrade your subscription by adding additional user seats.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeatLimitDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setSeatLimitDialogOpen(false);
              window.location.href = '/subscription';
            }}
            variant="contained"
            color="primary"
          >
            Upgrade Subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Dialog - New */}
      <Dialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upgrade Your Subscription</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Seat Limit Reached
            </Typography>
          </Alert>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Current Plan Details:
            </Typography>
            <Typography variant="body2">
              • Total Seats: {subscription?.subscription.totalUserSeats || 0}
            </Typography>
            <Typography variant="body2">
              • Used Seats: {subscription?.subscription.usedUserSeats || 0}
            </Typography>
            <Typography variant="body2">
              • Available Seats: {(subscription?.subscription.totalUserSeats || 0) - (subscription?.subscription.usedUserSeats || 0)}
            </Typography>
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Add more user seats to your subscription to continue adding team members. Each additional seat costs $50/month.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUpgradeDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowUpgradeDialog(false);
              window.location.href = '/subscription';
            }}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Add More Seats
          </Button>
        </DialogActions>
      </Dialog>
      </div>
    </MainLayout>
  );
};

export default Users;